import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRightIcon, Loader2 } from "lucide-react";
import SearchInput from "@/polymet/components/search-input";
import CategoryBadge from "@/polymet/components/category-badge";
import { Link } from "react-router-dom";

interface HeroSectionProps {
  title: string;
  subtitle: string;
  backgroundImage?: string;
  categories?: Array<{ label: string; count?: number }>;
  onSearch?: (query: string) => void;
  onCategoryClick?: (category: string) => void;
  primaryButtonText?: string;
  primaryButtonLink?: string;
  secondaryButtonText?: string;
  secondaryButtonLink?: string;
}

export default function HeroSection({
  title,
  subtitle,
  backgroundImage = "https://picsum.photos/seed/hero-machines/1920/1080",
  categories = [],
  onSearch,
  onCategoryClick,
  primaryButtonText,
  primaryButtonLink,
  secondaryButtonText,
  secondaryButtonLink,
}: HeroSectionProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const handleImageLoad = () => {
    setIsLoading(false);
    setHasError(false);
  };

  const handleImageError = () => {
    setHasError(true);
    setIsLoading(false);
  };

  return (
    <section className="relative w-full min-h-[350px] md:min-h-[400px] lg:min-h-[450px]">
      {/* Background Container */}
      <div className="absolute inset-0 z-0">
        
        {/* Loading State */}
        {isLoading && !hasError && (
          <div className="w-full h-full bg-gradient-to-br from-slate-800 via-slate-700 to-slate-900 flex items-center justify-center">
            <div className="text-white text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-3 text-orange-500" />
              <p className="text-base font-medium">Loading Hero Image...</p>
              <p className="text-xs opacity-75 mt-1">Please wait a moment</p>
            </div>
          </div>
        )}
        
        {/* Error State */}
        {hasError && (
          <div className="w-full h-full bg-gradient-to-br from-red-900 via-slate-800 to-slate-900 flex items-center justify-center">
            <div className="text-white text-center">
              <div className="text-red-400 mb-3">
                <svg className="h-8 w-8 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <p className="text-base font-medium">Image failed to load</p>
              <p className="text-xs opacity-75 mt-1">Using default background</p>
            </div>
          </div>
        )}
        
        {/* Background Image - Single load */}
        <img
          src={backgroundImage}
          alt="Industrial machines and equipment"
          className={`w-full h-full object-cover transition-opacity duration-700 ${
            isLoading ? 'opacity-0' : 'opacity-100'
          }`}
          loading="eager"
          decoding="async"
          onLoad={handleImageLoad}
          onError={handleImageError}
        />

        {/* Overlay - Always present */}
        <div className="absolute inset-0 bg-black/60" />
      </div>

      {/* Content - Optimized spacing */}
      <div className="relative z-10 container mx-auto px-4 py-8 md:py-12 lg:py-16">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h1 className="text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold mb-3 md:mb-4">
            {title}
          </h1>
          <p className="text-base md:text-lg lg:text-xl opacity-90 mb-6 md:mb-8 max-w-2xl mx-auto">
            {subtitle}
          </p>

          {/* Search input */}
          <div className="mb-4 md:mb-6">
            <SearchInput
              onSearch={onSearch}
              className="mx-auto"
              buttonText="Find Machines"
            />
          </div>

          {/* Categories */}
          {categories.length > 0 && (
            <div className="mb-6 md:mb-8">
              <div className="flex flex-wrap justify-center gap-2">
                {categories.map((category) => (
                  <CategoryBadge
                    key={category.label}
                    label={category.label}
                    count={category.count}
                    onClick={() =>
                      onCategoryClick && onCategoryClick(category.label)
                    }
                  />
                ))}
              </div>
            </div>
          )}

          {/* Buttons */}
          {(primaryButtonText || secondaryButtonText) && (
            <div className="flex flex-col sm:flex-row justify-center gap-3 md:gap-4">
              {primaryButtonText && primaryButtonLink && (
                <Button size="lg" className="gap-2 h-11 px-6" asChild>
                  <Link to={primaryButtonLink}>
                    {primaryButtonText}
                    <ArrowRightIcon className="h-4 w-4" />
                  </Link>
                </Button>
              )}
              {secondaryButtonText && secondaryButtonLink && (
                <Button
                  variant="outline"
                  size="lg"
                  className="bg-white/10 hover:bg-white/20 text-white border-white/30 h-11 px-6"
                  asChild
                >
                  <Link to={secondaryButtonLink}>{secondaryButtonText}</Link>
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
} 